import { useEffect, useState, useCallback } from 'react';
import { adminGet, adminPut, adminPost } from '../adminApi';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Card, Field, GREEN, mono } from './bfsiShared';

const VISIBILITY = [
  ['Field agent', 'Own active trip only'], ['Branch', 'Trips coming to it'],
  ['Control room', 'All live trips'], ['Auditor', 'Completed trips only'],
  ['Track0tic staff', 'None · status only'],
];

export default function BfsiRulesTab({ tenantId }) {
  const [rules, setRules] = useState(null);
  const [geofences, setGeofences] = useState([]);
  const [csv, setCsv] = useState('');

  const load = useCallback(() => {
    adminGet(`/customers/${tenantId}/lockbox-rules`).then(setRules).catch(e => toast.error(e.message));
    adminGet(`/customers/${tenantId}/geofences`).then(setGeofences).catch(e => toast.error(e.message));
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);

  const saveRules = async () => {
    try {
      const body = Object.fromEntries(Object.entries(rules).map(([k, v]) => [k, v === '' || v === null ? null : Number(v)]));
      setRules(await adminPut(`/customers/${tenantId}/lockbox-rules`, body));
      toast.success('Trip rules saved');
    } catch (e) { toast.error(e.message); }
  };

  const importCsv = async () => {
    try {
      const r = await adminPost(`/customers/${tenantId}/geofences/import`, { csv_text: csv, reason: 'Branch CSV import' });
      toast.success(`${r.imported} branch(es) imported — awaiting checker approval`);
      setCsv(''); load();
    } catch (e) { toast.error(e.message); }
  };

  if (!rules) return <div className="text-sm" style={{ color: '#9a9e96' }}>Loading…</div>;
  const r = (k) => (v) => setRules({ ...rules, [k]: v });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="bfsi-rules-tab">
      <div className="space-y-6">
        <Card>
          <h3 className="font-bold mb-1" style={{ color: '#1B1D1C' }}>Trip rules</h3>
          <p className="text-xs mb-4" style={{ color: '#9a9e96' }}>Breaches create alerts on the trip.</p>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Max trip duration (minutes)" value={rules.max_trip_minutes} onChange={r('max_trip_minutes')} testId="rule-max-trip" type="number" />
            <Field label="Max stop while sealed (minutes)" value={rules.max_sealed_stop_minutes} onChange={r('max_sealed_stop_minutes')} testId="rule-max-stop" type="number" />
            <Field label="Allowed route deviation (metres)" value={rules.max_route_deviation_m} onChange={r('max_route_deviation_m')} testId="rule-max-deviation" type="number" />
            <Field label="Offline allowed before alert (minutes)" value={rules.max_offline_minutes} onChange={r('max_offline_minutes')} testId="rule-max-offline" type="number" />
            <Field label="Weight change alarm (grams)" value={rules.weight_alarm_grams} onChange={r('weight_alarm_grams')} testId="rule-weight-alarm" type="number" />
          </div>
          <Button data-testid="bfsi-save-rules-btn" onClick={saveRules} className="mt-5 text-white" style={{ background: GREEN }}>Save trip rules</Button>
        </Card>

        <Card>
          <h3 className="font-bold mb-3" style={{ color: '#1B1D1C' }}>Who sees the route</h3>
          <table className="w-full text-sm">
            <tbody>
              {VISIBILITY.map(([who, what]) => (
                <tr key={who} className="border-t" style={{ borderColor: '#F0EEE8' }}>
                  <td className="py-2 font-medium" style={{ color: '#1B1D1C' }}>{who}</td>
                  <td className="py-2" style={{ color: '#6b7069' }}>{what}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs mt-3" style={{ color: '#9a9e96' }}>
            Passport QR on gold pouch stays — its public page is login-only for BFSI.
          </p>
        </Card>
      </div>

      <Card data-testid="bfsi-geofences-card">
        <h3 className="font-bold mb-1" style={{ color: '#1B1D1C' }}>Branches and geofences</h3>
        <p className="text-xs mb-4" style={{ color: '#9a9e96' }}>
          Branch names are stored under the customer's key; Track0tic staff see codes only.
          Every geofence change needs checker approval.
        </p>
        <table className="w-full text-sm mb-5">
          <thead>
            <tr className="text-left text-xs uppercase" style={{ color: '#9a9e96' }}>
              <th className="py-2">Branch code</th><th className="py-2">Name</th><th className="py-2">Geofence</th><th className="py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {geofences.length === 0 && <tr><td colSpan="4" className="py-4 text-center" style={{ color: '#9a9e96' }}>No branches yet</td></tr>}
            {geofences.map(g => (
              <tr key={g.org_unit_id} className="border-t" style={{ borderColor: '#F0EEE8' }} data-testid={`geofence-row-${g.branch_code}`}>
                <td className="py-2" style={mono}>{g.branch_code || '—'}</td>
                <td className="py-2">{g.name}</td>
                <td className="py-2" style={mono}>
                  {g.geofence === 'encrypted' ? '•••••• encrypted'
                    : g.geofence ? `${g.geofence.lat}, ${g.geofence.lon} · ${g.geofence.radius_m} m` : '—'}
                </td>
                <td className="py-2">
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                    style={g.geofence && g.geofence !== 'encrypted' ? { background: '#E3F1EC', color: GREEN } : { background: '#FDF3E0', color: '#9a6b12' }}>
                    {g.geofence && g.geofence !== 'encrypted' ? 'Approved' : 'Awaiting checker'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <label className="text-sm block mb-1" style={{ color: '#4b504a' }}>Import CSV (branch_code,name,lat,lon,radius_m)</label>
        <textarea data-testid="geofence-csv-input" value={csv} onChange={e => setCsv(e.target.value)} rows={4}
          placeholder={'branch_code,name,lat,lon,radius_m\nBR-001,Fort Branch,18.93,72.83,150'}
          className="w-full rounded-md border px-3 py-2 text-sm" style={{ borderColor: '#D9D6CD', ...mono }} />
        <Button data-testid="geofence-import-btn" onClick={importCsv} disabled={!csv.trim()}
          className="mt-3 text-white" style={{ background: GREEN }}>Import branches</Button>
      </Card>
    </div>
  );
}
